import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {AsyncPipe, Location} from '@angular/common';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {catchError, EMPTY, filter, map, Observable, switchMap, take} from 'rxjs';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {NotFoundComponent} from '../../../../shared/components/error/not-found/not-found.component';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {RouteConstants} from '../../../../shared/constants/route-constants';
import {LocalizedNumberPipe} from '../../../../shared/pipes/localized-number.pipe';
import {LocalizedPercentPipe} from '../../../../shared/pipes/localized-percent.pipe';
import {CisternActions} from '../../state/cistern.actions';
import {selectCisternById, selectIsCisternsLoading} from '../../state/cistern.selectors';
import {ApiCisternResponseDto} from '@birco/rainmesh-api';
import { TextWithIconComponent } from "../../../../shared/components/text-with-icon/text-with-icon.component";
import {CisternItemCardComponent} from '../../components/cistern-item-card/cistern-item-card.component';
import { MapWithLocationComponent } from "../../../map/components/map-with-location/map-with-location.component";
import {MapDevice} from '../../../map/models/map-device.interface';
import {selectAllDevices} from '../../../devices/state/device.selectors';
import {DeviceResponseDto} from '../../../devices/models/device-response.interface';
import {DeviceActions} from '../../../devices/state/device.actions';
import {DeviceItemCardComponent} from '../../../devices/components/device-item-card/device-item-card.component';
import {CisternDialogResult, DialogUpdateCisternComponent} from '../../components/dialog-update-cistern/dialog-update-cistern.component';
import {ConfirmDialogComponent, ConfirmDialogData} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ContextMenuComponent, ContextMenuData } from "../../../../shared/components/context-menu/context-menu.component";

@Component({
  selector: 'app-page-cistern-detail',
  imports: [
    CardComponent,
    IconComponent,
    TranslatePipe,
    AsyncPipe,
    LocalizedNumberPipe,
    LocalizedPercentPipe,
    NotFoundComponent,
    TextWithIconComponent,
    CisternItemCardComponent,
    MapWithLocationComponent,
    DeviceItemCardComponent,
    ContextMenuComponent
],
  templateUrl: './page-cistern-detail.component.html',
  styleUrl: './page-cistern-detail.component.scss'
})
export class PageCisternDetailComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private location = inject(Location);
  private translateService = inject(TranslateService);

  loading$ = this.store.select(selectIsCisternsLoading);
  cistern$!: Observable<ApiCisternResponseDto | null>;
  private devicesById: Record<string, DeviceResponseDto> = {};

  ngOnInit(): void {
    const cisternId$ = this.route.paramMap.pipe(
      map(params => params.get('id')),
      filter((id): id is string => !!id),
    );

    cisternId$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.store.dispatch(CisternActions.public.loadAllCisternsByFilters({}));
      this.store.dispatch(DeviceActions.public.loadAllDevicesByFilters({filter: {}}));
    });

    this.store.select(selectAllDevices).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((devices) => {
      this.devicesById = Object.fromEntries(devices.map((device) => [device.id, device]));
    });

    this.cistern$ = cisternId$.pipe(
      switchMap((id) => this.store.select(selectCisternById(id))),
      catchError(() => EMPTY),
    );

    this.route.queryParamMap.pipe(
      map((queryParams) => queryParams.get('edit') === 'true'),
      filter((shouldEdit) => shouldEdit),
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.cistern$.pipe(
        filter((cistern): cistern is ApiCisternResponseDto => !!cistern),
        take(1),
      ).subscribe((cistern) => {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {edit: null},
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        this.onEdit(cistern);
      });
    });
  }

  getError() {
    return {
      buttonLabelKey: 'cisterns.detail.error.goTo',
      buttonLink: [RouteConstants.routeCistern],
    };
  }

    getContextMenu(cistern: ApiCisternResponseDto): ContextMenuData[] {
      return [
        {
          labelKey: 'default.delete',
          action: () => this.onDelete(cistern)
        }
      ];
    }

  onBack(): void {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    void this.router.navigate(['/', RouteConstants.routeCistern]);
  }

  getMapDevice(cistern: ApiCisternResponseDto): MapDevice {
    const sensorId = cistern.distanceSensor?.id;
    const sensorDevice = sensorId ? this.devicesById[sensorId] : undefined;

    return {
      id: sensorId ?? cistern.id,
      name: cistern.distanceSensor?.name ?? cistern.name,
      latitude: sensorDevice?.location?.lat,
      longitude: sensorDevice?.location?.lon,
      type: sensorDevice?.type ?? cistern.distanceSensor?.type ?? null,
      isFavorite: sensorDevice?.isFavorite,
      isOutOfService: sensorDevice?.isOutOfService,
    };
  }

  getSensorDevice(cistern: ApiCisternResponseDto): DeviceResponseDto | null {
    const sensorId = cistern.distanceSensor?.id;
    if (!sensorId) {
      return null;
    }

    return this.devicesById[sensorId] ?? null;
  }

  onEdit(cistern: ApiCisternResponseDto) {
    const dialogRef = this.dialog.open(DialogUpdateCisternComponent, {
      width: '36rem',
      data: {mode: 'update', cistern},
    });

    dialogRef.afterClosed().pipe(
      take(1),
      filter((result): result is CisternDialogResult => !!result),
    ).subscribe((result) => {
      const id = result.id ?? cistern.id;
      this.store.dispatch(CisternActions.public.updateCistern({
        id: id,
        requestDto: result.requestDto,
      }));
    });
  }

  onDelete(cistern: ApiCisternResponseDto): void {
    const dialogData: ConfirmDialogData = {
      title: this.translateService.instant('cisterns.delete.title'),
      body: this.translateService.instant('cisterns.delete.body'),
      action: 'danger',
      confirmLabel: this.translateService.instant('default.delete'),
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().pipe(
      take(1),
      filter((result): result is true => result === true),
    ).subscribe(() => {
      this.store.dispatch(CisternActions.public.deleteCisternById({id: cistern.id}));
      void this.router.navigate(['/', RouteConstants.routeCistern]);
    });
  }

  protected readonly RouteConstants = RouteConstants;
}
